import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import AvailabilityUtils from "../../utils/availability/AvailabilityUtils";
import { getEventName, isPlaySpace } from "../../utils/spaceNamingUtils";
import "../../utils/testWebSocket";

import * as localService from "../../services/localService";
import spaceManagementService from "../../services/host-apiServices/spaceManagementService";
import billingService from "../../services/billingService";
import paymentFlowHelper from "../../utils/paymentFlowHelper";
import logger from "../../utils/logger";
import useBookingWebSocket from "../../hooks/useBookingWebSocket";
import "@splidejs/react-splide/css";
import { showApiMessage } from "../../utils/messageHandler";
import { Badge } from "../../shadcn/ui/badge";
import { Card, CardContent } from "../../shadcn/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../shadcn/ui/table";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Textarea } from "../../shadcn/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../../shadcn/ui/popover";
import { Calendar } from "../../shadcn/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../shadcn/ui/dialog";
import { SlidersHorizontal } from "lucide-react";
import SortDropdown from "../../components/SortDropdown";
import BookingFilterDialog from "../../components/BookingFilterDialog";
import PageHeader from "../../components/PageHeader";
import { getCurrentTimezoneOffset, convertToUTC } from "../../utils/timezoneUtils";
import { formatDateTime } from "../../utils/dateUtils";
import { canModify, canCheckIn, canCheckOut, canPayNow, canCancel } from "../../utils/bookingActionsUtils";
import LoadingSpinner from "../../components/LoadingSpinner";
import Pagination from "../../components/Pagination";
import {
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  User,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Circle,
  Search,
  X,
  Grid3x3,
  List as ListIcon,
  ChevronLeft,
  ChevronRight,
  Users,
  Building2,
  MoreVertical,
  Edit,
  Trash2,
  LogIn,
  LogOut,
  CreditCard,
} from "lucide-react";
import { FeatureAccess } from "../../utils/featureAccessControl";
import AddonsBreakdown from "../../components/addons/AddonsBreakdown";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../shadcn/ui/dropdown-menu";

// Helper function to transform API booking data to table format
const transformBookingData = (apiBooking, spaceData = null) => {
  logger.log("ndnksdnkdn 🔍 transformBookingData - Input:", {
    id: apiBooking.id,
    status: apiBooking.status,
    fullBooking: apiBooking,
  });

  const startTime = new Date(apiBooking.startTime);
  const endTime = new Date(apiBooking.endTime);
  const spaceInstance = apiBooking.spaceInstance || {};
  // Use provided spaceData if available, otherwise fallback to apiBooking.space
  const space = spaceData || apiBooking.space || {};
  const locationObj = spaceInstance.location || {};
  const building = locationObj.buildingAddress || {};
  const bookedBy = apiBooking.bookedBy || {};

  // Extract user info from emails array if bookedBy is null
  const primaryEmail = apiBooking.emails && apiBooking.emails.length > 0 ? apiBooking.emails[0] : "";
  const emailUsername = primaryEmail && typeof primaryEmail === "string" ? primaryEmail.split("@")[0] : "";

  // Images: rely on booking-level media first, then building images only
  const bookingMediaImages = apiBooking?.media?.images || [];
  const buildingImages = spaceInstance?.location?.buildingAddress?.images || [];

  const allSpaceImages = [...bookingMediaImages, ...buildingImages];

  // Filter out null, undefined, and empty strings, and normalize to strings
  const images = allSpaceImages
    .filter(img => {
      if (!img) return false;
      if (typeof img === "string") return img.trim() !== "";
      if (typeof img === "object" && img !== null) {
        return img.resourceUrl || img.url || img.src;
      }
      return false;
    })
    .map(img => {
      if (typeof img === "string") return img.trim();
      return (img.resourceUrl || img.url || img.src || "").trim();
    })
    .filter(img => img !== ""); // Remove any empty strings after trimming

  // Use space naming convention for title
  // The booking response may not include full space data, so we need to fetch it separately
  // PLAY services show their event name (stored as the instance title/name) — only for PLAY
  const isPlayService = isPlaySpace(space) || isPlaySpace(spaceInstance);
  const eventName = isPlayService ? getEventName(space, space?.instances?.[0]) || spaceInstance?.title || "" : "";

  // Try to get subcategory from space, otherwise use spaceInstance title or fallbacks
  const subCategoryName =
    space?.subCategory?.name ||
    space?.subCategoryName ||
    spaceInstance?.title ||
    space?.description ||
    spaceInstance?.description ||
    "Space";

  // Get capacity from space or spaceInstance
  const capacity = space?.capacity || spaceInstance?.capacity || 0;

  // Get address from spaceInstance location
  const address = building?.streetAddress || locationObj?.name || building?.city || "Unknown Location";

  // Generate space name using the convention: "Subcategory for Capacity at Address" or "Subcategory at Address"
  let spaceName;
  if (eventName) {
    spaceName = eventName;
  } else if (capacity > 1) {
    spaceName = `${subCategoryName} for ${capacity} at ${address}`;
  } else {
    spaceName = `${subCategoryName} at ${address}`;
  }

  const result = {
    ...apiBooking,
    status: apiBooking.status, // Explicitly preserve status
    title: spaceName,
    location: building.streetAddress
      ? `${building.streetAddress}, ${building.city || ""}, ${building.region || ""}, ${building.country || ""}`
      : locationObj.name || "Location not specified",
    user: bookedBy.firstName
      ? `${bookedBy.firstName} ${bookedBy.lastName || ""}`.trim()
      : bookedBy.username || emailUsername || "Guest User",
    email: bookedBy.email || primaryEmail || "",
    avatar:
      bookedBy.imageUrl ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(
        bookedBy.firstName || bookedBy.username || emailUsername || "Guest"
      )}&background=random`,
    startTime,
    endTime,
    spaceImages: images,
    spaceDescription: space?.description || spaceInstance?.description || "",
    spaceCapacity: space?.capacity || spaceInstance?.capacity || 0,
    spacePrice: space?.ratePrice || spaceInstance?.ratePrice || 0,
    spaceCurrency: space?.currency || spaceInstance?.currency || "KES",
  };

  logger.log("ndnksdnkdn 🔍 transformBookingData - Output:", {
    id: result.id,
    status: result.status,
    title: result.title,
  });

  return result;
};

const BOOKING_SORT_OPTIONS = [
  { field: "createdAt", label: "Booked at" },
  { field: "startTime", label: "Upcoming" },
];

const Booking = () => {
  const [totalPageSize, setTotalPageSize] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(21);
  const [bookingStatus, setBookingStatus] = useState("ALL");
  const [searchValue, setSearchValue] = useState("");
  const [bookings, setBookings] = useState([]);
  const [todaysBookingsData, setTodaysBookingsData] = useState([]);
  const [loadingTodays, setLoadingTodays] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState(`${BOOKING_SORT_OPTIONS[0].field}:desc`);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"
  const [timeFilter, setTimeFilter] = useState("all"); // "upcoming", "active", "past", "all"
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const bookingDetailsScrollRef = useRef(null);
  const [heroParallax, setHeroParallax] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editFormData, setEditFormData] = useState({
    startDate: null,
    startTime: "",
    endDate: null,
    endTime: "",
    emails: [],
    notes: "",
  });
  const [editOriginalDurationMs, setEditOriginalDurationMs] = useState(null);
  const [editingSpace, setEditingSpace] = useState(null);

  // Calendar day coloring for edit modal — green on days the space is open
  const editCalendarModifiersStyles = useMemo(
    () => ({
      available: { backgroundColor: "#dcfce7", color: "#166534", fontWeight: "bold" },
      unavailable: { backgroundColor: "#f3f4f6", color: "#9ca3af" },
    }),
    []
  );

  const editCalendarModifiers = useMemo(() => {
    if (!editingSpace?.availableSlots?.length) return {};
    const dayNames = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    const availableDayNames = new Set(editingSpace.availableSlots.map(s => s.day));
    return {
      available: date => availableDayNames.has(dayNames[date.getDay()]),
      unavailable: date => !availableDayNames.has(dayNames[date.getDay()]),
    };
  }, [editingSpace]);

  // Helper to maintain duration when editing
  const maintainDuration = useCallback(
    (newStart, newEnd, changedField) => {
      if (!editOriginalDurationMs) return { start: newStart, end: newEnd };

      const duration = editOriginalDurationMs;
      let start = new Date(newStart);
      let end = new Date(newEnd);

      if (changedField === "start") {
        end = new Date(start.getTime() + duration);
      } else if (changedField === "end") {
        start = new Date(end.getTime() - duration);
      }

      return { start, end };
    },
    [editOriginalDurationMs]
  );

  const handleEditStartDateChange = date => {
    if (!date) return;
    const { start, end } = maintainDuration(date, editFormData.endDate, "start");
    setEditFormData(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      startTime: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
      endTime: `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`,
    }));
  };

  const handleEditStartTimeChange = timeStr => {
    const [h, m] = timeStr.split(":").map(Number);
    const newStart = new Date(editFormData.startDate);
    newStart.setHours(h, m, 0, 0);

    const { start, end } = maintainDuration(newStart, editFormData.endDate, "start");
    setEditFormData(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      startTime: timeStr,
      endTime: `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`,
    }));
  };

  const handleEditEndDateChange = date => {
    if (!date) return;
    const { start, end } = maintainDuration(editFormData.startDate, date, "end");
    setEditFormData(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      startTime: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
      endTime: `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`,
    }));
  };

  const handleEditEndTimeChange = timeStr => {
    const [h, m] = timeStr.split(":").map(Number);
    const newEnd = new Date(editFormData.endDate);
    newEnd.setHours(h, m, 0, 0);

    const { start, end } = maintainDuration(editFormData.startDate, newEnd, "end");
    setEditFormData(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      startTime: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
      endTime: timeStr,
    }));
  };
  const [emailInput, setEmailInput] = useState("");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [optimisticUpdates, setOptimisticUpdates] = useState({}); // Track optimistic status changes by booking ID
  // Map of subCategoryId → { name, categoryId } — built once at mount, never causes re-renders
  const subcategoryMapRef = useRef({});

  // Helper function to optimistically update booking status across all state arrays
  const optimisticallyUpdateStatus = useCallback(
    (bookingId, newStatus) => {
      // Store the original status for potential rollback
      setOptimisticUpdates(prev => ({
        ...prev,
        [bookingId]: {
          newStatus,
          timestamp: Date.now(),
        },
      }));

      // Update bookings array
      setBookings(prevBookings =>
        prevBookings.map(booking =>
          String(booking.id) === String(bookingId) ? { ...booking, status: newStatus } : booking
        )
      );

      // Update today's bookings array
      setTodaysBookingsData(prevBookings =>
        prevBookings.map(booking =>
          String(booking.id) === String(bookingId) ? { ...booking, status: newStatus } : booking
        )
      );

      // Update booking details if open
      if (bookingDetails && String(bookingDetails.id) === String(bookingId)) {
        setBookingDetails(prev => ({ ...prev, status: newStatus }));
      }
    },
    [bookingDetails]
  );

  // Helper function to rollback optimistic update on error
  const rollbackOptimisticUpdate = useCallback(
    (bookingId, originalStatus) => {
      // Clear the optimistic update
      setOptimisticUpdates(prev => {
        const updated = { ...prev };
        delete updated[bookingId];
        return updated;
      });

      // Revert bookings array
      setBookings(prevBookings =>
        prevBookings.map(booking =>
          String(booking.id) === String(bookingId) ? { ...booking, status: originalStatus } : booking
        )
      );

      // Revert today's bookings array
      setTodaysBookingsData(prevBookings =>
        prevBookings.map(booking =>
          String(booking.id) === String(bookingId) ? { ...booking, status: originalStatus } : booking
        )
      );

      // Revert booking details if open
      if (bookingDetails && String(bookingDetails.id) === String(bookingId)) {
        setBookingDetails(prev => ({ ...prev, status: originalStatus }));
      }
    },
    [bookingDetails]
  );

  // WebSocket for real-time booking updates
  useBookingWebSocket(
    useCallback(
      update => {
        logger.log("🔴 WEBSOCKET UPDATE RECEIVED:", update);
        logger.log("🔴 Booking ID:", update.bookingId);
        logger.log("🔴 Status change:", update.oldStatus, "→", update.newStatus);

        // Clear the optimistic update since WebSocket confirmed the change
        setOptimisticUpdates(prev => {
          const updated = { ...prev };
          delete updated[update.bookingId];
          return updated;
        });

        setBookings(prevBookings => {
          logger.log("🔴 Current bookings count:", prevBookings.length);
          const updated = prevBookings.map(booking => {
            if (String(booking.id) === String(update.bookingId)) {
              logger.log("🔴 FOUND MATCHING BOOKING! Updating status from", booking.status, "to", update.newStatus);
              return { ...booking, status: update.newStatus };
            }
            return booking;
          });
          logger.log("🔴 Updated bookings:", updated);
          return updated;
        });

        setTodaysBookingsData(prevBookings =>
          prevBookings.map(booking =>
            String(booking.id) === String(update.bookingId) ? { ...booking, status: update.newStatus } : booking
          )
        );

        if (bookingDetails && String(bookingDetails.id) === String(update.bookingId)) {
          logger.log("🔴 Updating booking details modal");
          setBookingDetails(prev => ({ ...prev, status: update.newStatus }));
        }

        if (update.message) {
          showApiMessage({ status: 200, data: { message: update.message } }, "booking update");
        }
      },
      [bookingDetails]
    )
  );

  useEffect(() => {
    const currentUser = localService.getUser();
    if (!currentUser) {
      logger.error("No user found");
      setCurrentUserRole(null);
      return;
    }
    setCurrentUserRole(currentUser.role || "USER");
  }, []);

  // Build subcategory lookup map once at mount:
  // { [subCategoryId]: { name, categoryId } }
  // This avoids needing categoryId when displaying a booking's subcategory.
  useEffect(() => {
    const buildSubcategoryMap = async () => {
      try {
        const catRes = await billingService.getServiceCategories();
        const categories = catRes?.data?.content || catRes?.data || [];
        if (!Array.isArray(categories) || categories.length === 0) return;

        // Fetch all subcategory lists in parallel
        const results = await Promise.allSettled(categories.map(cat => billingService.getServiceSubcategories(cat.id)));

        const map = {};
        results.forEach((result, i) => {
          if (result.status !== "fulfilled") return;
          const subcats = result.value?.data?.content || result.value?.data || [];
          if (!Array.isArray(subcats)) return;
          const cat = categories[i];
          subcats.forEach(sub => {
            map[sub.id] = { name: sub.name, categoryId: cat.id, categoryName: cat.name };
          });
        });

        subcategoryMapRef.current = map;
        logger.info(`Subcategory map built: ${Object.keys(map).length} entries`);
      } catch (err) {
        logger.warn("Failed to build subcategory map:", err);
      }
    };
    buildSubcategoryMap();
  }, []); // run once on mount
  const handleDetailsScroll = useCallback(e => {
    const y = e.currentTarget?.scrollTop || 0;
    setHeroParallax(Math.min(y * 0.6, 220));
  }, []);

  const fetchBookings = async (page = 1, searchTerm = appliedSearch) => {
    const currentUser = localService.getUser();
    if (!currentUser) {
      logger.error("No user found");
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const timezone = getCurrentTimezoneOffset();
      const sortParam = getSortParams();

      // Build query params - only include status if not "ALL"
      const queryParams = {
        page: Math.max(page - 1, 0),
        size: 20,
        sort: sortParam,
      };

      if (bookingStatus !== "ALL") {
        queryParams.status = bookingStatus.toUpperCase();
      }

      if (searchTerm) {
        // Use specific search parameters for comprehensive searching across multiple fields
        queryParams.addressContains = searchTerm;
        queryParams.buildingName = searchTerm;
        queryParams.hostOrgName = searchTerm;
        queryParams.bookingOwnerEmail = searchTerm;
        queryParams.search = searchTerm; // Keep generic search as fallback
      }

      const res = await spaceManagementService.searchBookings(queryParams, timezone);
      if (res.status === 200) {
        setTotalPageSize(res.data?.totalElements ?? 0);
        if (Number.isFinite(res.data?.size) && res.data.size > 0) {
          setItemsPerPage(res.data.size);
        }

        const bookings = res.data?.content || [];
        logger.log("ndnksdnkdn 🔍 API Response - Total bookings:", bookings.length);
        logger.log("ndnksdnkdn 🔍 API Response - First booking:", bookings[0]);
        logger.log("ndnksdnkdn 🔍 API Response - First booking status:", bookings[0]?.status);

        const transformedBookings = bookings.map(booking => transformBookingData(booking));
        logger.log("ndnksdnkdn 🔍 Transformed - First booking:", transformedBookings[0]);
        logger.log("ndnksdnkdn 🔍 Transformed - First booking status:", transformedBookings[0]?.status);

        setBookings(transformedBookings);
      }
    } catch (err) {
      logger.error("Failed to fetch bookings:", err);
      showApiMessage(err, "fetch reservations");
    } finally {
      setLoading(false);
    }
  };

  const fetchTodaysBookings = async (searchTerm = appliedSearch) => {
    const currentUser = localService.getUser();
    if (!currentUser) {
      logger.error("No user found");
      setLoadingTodays(false);
      setTodaysBookingsData([]);
      return;
    }

    setLoadingTodays(true);

    try {
      const timezone = getCurrentTimezoneOffset();
      const sortParam = getSortParams();
      const { startTimeFrom, startTimeTo } = getTodayDateRange();

      const queryParams = {
        page: 0,
        size: 20,
        sort: sortParam,
        startTimeFrom,
        startTimeTo,
      };

      // Don't apply status filter for today's bookings - always fetch all

      if (searchTerm) {
        // Use specific search parameters for comprehensive searching across multiple fields
        queryParams.addressContains = searchTerm;
        queryParams.buildingName = searchTerm;
        queryParams.hostOrgName = searchTerm;
        queryParams.bookingOwnerEmail = searchTerm;
        queryParams.search = searchTerm; // Keep generic search as fallback
      }

      const res = await spaceManagementService.searchBookings(queryParams, timezone);
      if (res.status === 200) {
        const bookings = res.data?.content || [];
        const transformedBookings = bookings.map(booking => transformBookingData(booking));
        setTodaysBookingsData(transformedBookings);
      }
    } catch (err) {
      logger.error("Failed to fetch today's bookings:", err);
      setTodaysBookingsData([]);
    } finally {
      setLoadingTodays(false);
    }
  };

  useEffect(() => {
    // Call fetchBookings whenever dependencies change
    fetchBookings(currentPage, appliedSearch);
    // eslint-disable-next-line
  }, [currentUserRole, bookingStatus, currentPage, sortOption, appliedSearch]);

  useEffect(() => {
    fetchTodaysBookings(appliedSearch);
    // eslint-disable-next-line
  }, [currentUserRole, sortOption, appliedSearch]);

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    setAppliedSearch("");
    setCurrentPage(1);
  }, []);

  const performSearch = e => {
    e.preventDefault();
    setCurrentPage(1);
    setAppliedSearch(searchValue.trim());
  };

  const getStatusConfig = status => {
    const statusConfig = {
      PENDING_PAYMENT: {
        variant: "default",
        color: "bg-amber-50 text-amber-700 border-amber-200",
        icon: CreditCard,
        dotColor: "bg-amber-500",
      },
      PENDING: {
        variant: "default",
        color: "bg-yellow-50 text-yellow-700 border-yellow-200",
        icon: AlertCircle,
        dotColor: "bg-yellow-500",
      },
      CONFIRMED: {
        variant: "default",
        color: "bg-green-50 text-green-700 border-green-200",
        icon: CheckCircle2,
        dotColor: "bg-green-500",
      },
      CHECKED_IN: {
        variant: "default",
        color: "bg-blue-50 text-blue-700 border-blue-200",
        icon: CheckCircle2,
        dotColor: "bg-blue-500",
      },
      CANCELED: {
        variant: "destructive",
        color: "bg-red-50 text-red-700 border-red-200",
        icon: XCircle,
        dotColor: "bg-red-500",
      },
      CANCELLED: {
        variant: "destructive",
        color: "bg-red-50 text-red-700 border-red-200",
        icon: XCircle,
        dotColor: "bg-red-500",
      },
      COMPLETED: {
        variant: "secondary",
        color: "bg-green-50 text-green-700 border-green-200",
        icon: CheckCircle2,
        dotColor: "bg-green-500",
      },
      NO_SHOW: {
        variant: "default",
        color: "bg-red-50 text-red-700 border-red-200",
        icon: AlertCircle,
        dotColor: "bg-red-500",
      },
    };
    return (
      statusConfig[status] || {
        variant: "secondary",
        color: "bg-gray-50 text-gray-700 border-gray-200",
        icon: Circle,
        dotColor: "bg-gray-500",
      }
    );
  };

  const changePage = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Check if a booking is today
  const isToday = date => {
    const today = new Date();
    const bookingDate = new Date(date);
    return (
      bookingDate.getDate() === today.getDate() &&
      bookingDate.getMonth() === today.getMonth() &&
      bookingDate.getFullYear() === today.getFullYear()
    );
  };

  const getSortParams = () => {
    const sortValue = sortOption || `${BOOKING_SORT_OPTIONS[0].field}:desc`;
    const [sortByField = "createdAt", sortDirection = "desc"] = (sortValue || "").split(":");
    return `${sortByField},${sortDirection}`;
  };

  const getTodayDateRange = () => {
    const today = new Date();
    const startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);

    return {
      startTimeFrom: convertToUTC(startOfDay),
      startTimeTo: convertToUTC(endOfDay),
    };
  };

  const applyStatusAndTimeFilters = data => {
    if (!Array.isArray(data)) return [];
    const now = new Date();
    return data.filter(booking => {
      if (bookingStatus !== "ALL" && booking.status !== bookingStatus) {
        return false;
      }

      if (timeFilter === "all") {
        return true;
      }

      const startTime = new Date(booking.startTime);
      const endTime = new Date(booking.endTime);

      if (timeFilter === "upcoming") {
        return startTime > now;
      } else if (timeFilter === "active") {
        return startTime <= now && endTime >= now;
      } else if (timeFilter === "past") {
        return endTime < now;
      }

      return true;
    });
  };

  const sortByCreatedAtDesc = data => {
    const source = Array.isArray(data) ? data : [];
    return [...source].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
  };

  // Filter bookings by status and time - memoized to recalculate when filters or bookings change
  const filteredBookings = useMemo(() => {
    return applyStatusAndTimeFilters(bookings);
  }, [bookings, bookingStatus, timeFilter]);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return bookingStatus !== "ALL" || timeFilter !== "all";
  }, [bookingStatus, timeFilter]);

  // Separate today's bookings - only show when no filters are applied
  const filteredTodaysBookings = useMemo(() => {
    if (hasActiveFilters) return [];
    return todaysBookingsData.filter(booking => isToday(booking.startTime));
  }, [todaysBookingsData, hasActiveFilters]);

  const otherBookings = useMemo(() => {
    return filteredBookings.filter(booking => !isToday(booking.startTime));
  }, [filteredBookings]);

  // Update active filters display
  useEffect(() => {
    const filters = [];

    if (bookingStatus !== "ALL") {
      const statusLabels = {
        PENDING: "Pending",
        PENDING_PAYMENT: "Pending Payment",
        CHECKED_IN: "Checked In",
        CANCELED: "Canceled",
        COMPLETED: "Completed",
        NO_SHOW: "No Show",
      };
      filters.push({
        id: "status",
        label: statusLabels[bookingStatus] || bookingStatus,
        value: bookingStatus,
      });
    }

    if (timeFilter !== "all") {
      const timeLabels = {
        upcoming: "Upcoming",
        active: "Active",
        past: "Past",
      };
      filters.push({
        id: "timeFilter",
        label: timeLabels[timeFilter] || timeFilter,
        value: timeFilter,
      });
    }

    setActiveFilters(filters);
  }, [bookingStatus, timeFilter]);

  // Filter handlers
  const applyFilters = filterState => {
    setBookingStatus(filterState.status);
    setTimeFilter(filterState.timeFilter);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setBookingStatus("ALL");
    setTimeFilter("all");
    setCurrentPage(1);
  };

  const clearFilter = filterId => {
    if (filterId === "status") {
      setBookingStatus("ALL");
    } else if (filterId === "timeFilter") {
      setTimeFilter("all");
    }
    setCurrentPage(1);
  };

  // Handle booking card click
  const handleBookingClick = async booking => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
    setLoadingDetails(true);
    setBookingDetails(null);

    try {
      const response = await spaceManagementService.getBookingById(booking.id);
      if (response?.status === 200 && response?.data) {
        const rawBooking = response.data;
        logger.log("DEBUG: rawBooking structure =", JSON.stringify(rawBooking, null, 2));
        logger.log("DEBUG: subcategoryMap =", JSON.stringify(subcategoryMapRef.current, null, 2));
        const spaceData = rawBooking.space || null;
        const transformedBooking = transformBookingData(rawBooking, spaceData);

        // Resolve subcategory name if not already embedded in the space object
        const hasSubCategoryName = !!(spaceData?.subCategory?.name || spaceData?.subCategoryName);
        const subCategoryId = spaceData?.subCategory?.id || spaceData?.subCategoryId || rawBooking?.subCategoryId;

        if (!hasSubCategoryName && subCategoryId) {
          const entry = subcategoryMapRef.current[subCategoryId];
          logger.log(`DEBUG: Found entry for subCategoryId ${subCategoryId} =`, entry);
          if (entry?.name) {
            const capacity = transformedBooking.spaceCapacity;
            const address =
              rawBooking?.spaceInstance?.location?.buildingAddress?.streetAddress ||
              rawBooking?.spaceInstance?.location?.name ||
              "";
            const newTitle =
              capacity > 1 ? `${entry.name} for ${capacity} at ${address}` : `${entry.name} at ${address}`;
            transformedBooking.title = newTitle || transformedBooking.title;
            transformedBooking.subCategoryName = entry.name;
          }
        }

        setBookingDetails(transformedBooking);
      } else {
        showApiMessage({ message: "Failed to load booking details" }, "load booking details");
        setBookingDetails(booking);
      }
    } catch (error) {
      logger.error("Error fetching booking details:", error);
      showApiMessage(error, "load booking details");
      setBookingDetails(booking);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Action handlers
  const handleEditBooking = async booking => {
    // Validate that the booking can be modified before opening the edit modal
    if (!canModify(booking)) {
      const status = booking?.status?.toUpperCase();
      let message = "This booking cannot be modified.";

      if (status === "CHECKED_IN") {
        message = "Cannot edit booking after check-in. Please check out the guest first.";
      } else if (["CANCELLED", "CANCELED"].includes(status)) {
        message = "Cannot edit a cancelled booking.";
      } else if (status === "COMPLETED") {
        message = "Cannot edit a completed booking.";
      } else if (status === "NO_SHOW") {
        message = "Cannot edit a no-show booking.";
      } else if (status === "PENDING_PAYMENT") {
        message = "Cannot edit booking while payment is pending. Please complete or cancel the payment first.";
      }

      showApiMessage(
        {
          response: {
            status: 400,
            data: { message },
          },
        },
        "edit booking"
      );
      return;
    }

    const isCheckedIn = booking.status === "CHECKED_IN";
    setEditingBooking(booking);
    setShowEditModal(true);

    // Fetch full booking details if needed
    try {
      const response = await spaceManagementService.getBookingById(booking.id);
      if (response?.status === 200 && response?.data) {
        const bookingData = response.data;
        let startTime = new Date(bookingData.startTime);
        let endTime = new Date(bookingData.endTime);

        // Store original duration (do not allow increases)
        const originalDuration = Math.max(0, endTime.getTime() - startTime.getTime());
        setEditOriginalDurationMs(originalDuration);

        // Check if space is daily or monthly and apply default times based on category
        const space = bookingData.space || bookingData.spaceInstance;
        setEditingSpace(space || null);
        if (space) {
          const durationType = space.duration || space.bookingDuration || "HOURLY";
          const categoryName = space.category?.name || space.categoryName || "WORK";

          if (durationType === "DAILY" || durationType === "MONTHLY") {
            const defaultRange = AvailabilityUtils.getDefaultTimeRange(categoryName, durationType, startTime);
            if (defaultRange.start && defaultRange.end) {
              startTime = defaultRange.start;
              endTime = defaultRange.end;
            }
          }
        }

        setEditFormData({
          startDate: new Date(startTime),
          startTime: `${String(startTime.getHours()).padStart(2, "0")}:${String(startTime.getMinutes()).padStart(2, "0")}`,
          endDate: new Date(endTime),
          endTime: `${String(endTime.getHours()).padStart(2, "0")}:${String(endTime.getMinutes()).padStart(2, "0")}`,
          emails: bookingData.emails || [],
          notes: bookingData.notes || "",
        });
        setEmailInput("");
      } else {
        // Fallback to card data
        let startTime = new Date(booking.startTime);
        let endTime = new Date(booking.endTime);

        // Store original duration from fallback data
        const originalDuration = Math.max(0, endTime.getTime() - startTime.getTime());
        setEditOriginalDurationMs(originalDuration);

        // Check if space is daily or monthly and apply default times based on category
        const space = booking.space || booking.spaceInstance;
        setEditingSpace(space || null);
        if (space) {
          const durationType = space.duration || space.bookingDuration || "HOURLY";
          const categoryName = space.category?.name || space.categoryName || "WORK";

          if (durationType === "DAILY" || durationType === "MONTHLY") {
            const defaultRange = AvailabilityUtils.getDefaultTimeRange(categoryName, durationType, startTime);
            if (defaultRange.start && defaultRange.end) {
              startTime = defaultRange.start;
              endTime = defaultRange.end;
            }
          }
        }

        setEditFormData({
          startDate: new Date(startTime),
          startTime: `${String(startTime.getHours()).padStart(2, "0")}:${String(startTime.getMinutes()).padStart(2, "0")}`,
          endDate: new Date(endTime),
          endTime: `${String(endTime.getHours()).padStart(2, "0")}:${String(endTime.getMinutes()).padStart(2, "0")}`,
          emails: booking.emails || [],
          notes: booking.notes || "",
        });
        setEmailInput("");
      }
    } catch (error) {
      logger.error("Error fetching booking for edit:", error);
      // Fallback to card data
      let startTime = new Date(booking.startTime);
      let endTime = new Date(booking.endTime);

      // Check if space is daily or monthly and apply default times based on category
      const space = booking.space || booking.spaceInstance;
      setEditingSpace(space || null);
      if (space) {
        const durationType = space.duration || space.bookingDuration || "HOURLY";
        const categoryName = space.category?.name || space.categoryName || "WORK";

        if (durationType === "DAILY" || durationType === "MONTHLY") {
          const defaultRange = AvailabilityUtils.getDefaultTimeRange(categoryName, durationType, startTime);
          if (defaultRange.start && defaultRange.end) {
            startTime = defaultRange.start;
            endTime = defaultRange.end;
          }
        }
      }

      setEditFormData({
        startDate: new Date(startTime),
        startTime: `${String(startTime.getHours()).padStart(2, "0")}:${String(startTime.getMinutes()).padStart(2, "0")}`,
        endDate: new Date(endTime),
        endTime: `${String(endTime.getHours()).padStart(2, "0")}:${String(endTime.getMinutes()).padStart(2, "0")}`,
        emails: booking.emails || [],
        notes: booking.notes || "",
      });
      setEmailInput("");
    }
  };

  const handleSaveEdit = async () => {
    if (!editingBooking) return;

    try {
      const timezone = getCurrentTimezoneOffset();

      const combineDateTime = (dateObj, timeStr) => {
        if (!dateObj) return null;
        const [hours = "00", minutes = "00"] = (timeStr || "").split(":");
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        return new Date(`${year}-${month}-${day}T${hours}:${minutes}`);
      };

      const startDateTime = combineDateTime(editFormData.startDate, editFormData.startTime);
      const endDateTime = combineDateTime(editFormData.endDate, editFormData.endTime);

      if (!startDateTime || !endDateTime || isNaN(startDateTime) || isNaN(endDateTime)) {
        showApiMessage({ message: "Please select valid start and end date/time" }, "update booking");
        return;
      }

      const newStartTime = startDateTime.toISOString();
      const newEndTime = endDateTime.toISOString();

      // Enforce duration not exceeding original
      const originalDurationMs =
        editOriginalDurationMs ??
        Math.max(0, new Date(editingBooking.endTime).getTime() - new Date(editingBooking.startTime).getTime());
      const newDurationMs = new Date(newEndTime).getTime() - new Date(newStartTime).getTime();

      if (newDurationMs <= 0) {
        showApiMessage({ message: "End time must be after start time" }, "update booking");
        return;
      }

      if (newDurationMs > originalDurationMs) {
        showApiMessage({ message: "New booking duration cannot exceed the original duration" }, "update booking");
        return;
      }

      const updatePayload = {
        newStartTime,
        newEndTime,
        emails: editFormData.emails,
        notes: editFormData.notes,
      };

      const res = await spaceManagementService.updateBooking(editingBooking.id, updatePayload, timezone);
      if (res.status === 200 || res.status === 204) {
        showApiMessage({ status: 200, data: { message: "Booking updated successfully" } }, "update booking");
        setShowEditModal(false);
        setEditingBooking(null);
        fetchBookings(currentPage);
      }
    } catch (err) {
      logger.error("Failed to update booking:", err);
      showApiMessage(err, "update booking");
    }
  };

  const handleAddEmail = () => {
    if (emailInput.trim() && !editFormData.emails.includes(emailInput.trim())) {
      setEditFormData({
        ...editFormData,
        emails: [...editFormData.emails, emailInput.trim()],
      });
      setEmailInput("");
    }
  };

  const handleRemoveEmail = emailToRemove => {
    setEditFormData({
      ...editFormData,
      emails: editFormData.emails.filter(email => email !== emailToRemove),
    });
  };

  const handleCancelBooking = booking => {
    setBookingToCancel(booking);
    setShowCancelDialog(true);
  };

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;

    // Store original status for rollback
    const originalStatus = bookingToCancel.status;

    setIsCancelling(true);

    // Optimistically update UI
    optimisticallyUpdateStatus(bookingToCancel.id, "CANCELLED");

    try {
      const res = await spaceManagementService.cancelBooking(bookingToCancel.id);
      if (res.status === 200 || res.status === 201) {
        const successMessage =
          (typeof res.data === "string" && res.data) || res?.data?.message || "Booking cancelled successfully";
        showApiMessage({ status: 200, data: { message: successMessage } }, "cancel booking");
        setShowCancelDialog(false);
        setBookingToCancel(null);
        // Don't need to fetch - WebSocket will confirm or optimistic update is already shown
      }
    } catch (err) {
      logger.error("Failed to cancel booking:", err);
      // Rollback on error
      rollbackOptimisticUpdate(bookingToCancel.id, originalStatus);
      showApiMessage(err, "cancel booking");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCheckIn = async booking => {
    // Validate check-in time window
    const now = new Date();
    const start = new Date(booking.startTime);
    const end = new Date(booking.endTime);

    if (now < start) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Cannot check in yet. Please wait until the booking starts." },
          },
        },
        "check in"
      );
      return;
    }

    if (now > end) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Cannot check in. The booking has already ended." },
          },
        },
        "check in"
      );
      return;
    }

    // Store original status for rollback
    const originalStatus = booking.status;

    // Optimistically update UI
    optimisticallyUpdateStatus(booking.id, "CHECKED_IN");

    try {
      const res = await spaceManagementService.checkInBooking(booking.id);
      if (res.status === 200 || res.status === 201) {
        showApiMessage({ message: "Checked in successfully" }, "check in");
        // Don't need to fetch - WebSocket will confirm or optimistic update is already shown
      }
    } catch (err) {
      logger.error("Failed to check in:", err);
      // Rollback on error
      rollbackOptimisticUpdate(booking.id, originalStatus);
      showApiMessage(err, "check in");
    }
  };

  const handleCheckOut = async booking => {
    // Store original status for rollback
    const originalStatus = booking.status;

    // Optimistically update UI
    optimisticallyUpdateStatus(booking.id, "COMPLETED");

    try {
      const res = await spaceManagementService.checkOutBooking(booking.id);
      if (res.status === 200 || res.status === 201) {
        showApiMessage({ message: "Checked out successfully" }, "check out");
        // Don't need to fetch - WebSocket will confirm or optimistic update is already shown
      }
    } catch (err) {
      logger.error("Failed to check out:", err);
      // Rollback on error
      rollbackOptimisticUpdate(booking.id, originalStatus);
      showApiMessage(err, "check out");
    }
  };

  const handlePayBooking = async booking => {
    try {
      setIsProcessingPayment(true);

      // Check for offline ticket redirect (PLAY services)
      const isPlayService =
        booking?.isPlayService ||
        booking?.space?.category?.name === "Play" ||
        booking?.spaceInstance?.category?.name === "Play" ||
        (booking?.tickets && booking?.tickets?.length > 0);

      const ticketUrl = booking?.ticketUrl || (booking?.tickets?.length > 0 ? booking.tickets[0].url : null);
      const ticketType = booking?.tickets?.length > 0 ? booking.tickets[0].ticketType : "OFFLINE"; // Default to OFFLINE if url is present but no type

      if (isPlayService && ticketUrl && ticketType !== "DEFAULT") {
        let finalUrl = ticketUrl;
        if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
          finalUrl = "https://" + finalUrl;
        }
        window.open(finalUrl, "_blank");
        setIsProcessingPayment(false);
        return;
      }

      const currentUser = localService.getUser();
      if (!currentUser?.organizationId) {
        showApiMessage({ response: { status: 400, data: { message: "Organization ID not found" } } }, "pay booking");
        setIsProcessingPayment(false);
        return;
      }

      // Get invoice by organizationId and productType, then filter for the booking reference
      // First try exact match for legacy invoices
      let invoiceResponse = await billingService.getInvoices({
        clientReference: booking.id.toString(),
        organizationId: currentUser.organizationId,
        productType: "DIRECT_BOOKING",
        limit: 1,
      });

      // If no legacy invoice found, try to find one with the new prefix/uuid format
      if (!invoiceResponse.data?.data || invoiceResponse.data.data.length === 0) {
        const orgInvoices = await billingService.getInvoices({
          organizationId: currentUser.organizationId,
          productType: "DIRECT_BOOKING",
          limit: 50, // Get recent ones to filter
        });

        const data = orgInvoices.data?.data || [];
        const matchingInvoice = data.find(
          inv =>
            inv.clientReference &&
            (inv.clientReference.startsWith(`BK-${booking.id}-`) || inv.clientReference.startsWith(`BK${booking.id}`))
        );

        if (matchingInvoice) {
          invoiceResponse = { data: { data: [matchingInvoice] } };
        }
      }

      if (
        !invoiceResponse.data?.data ||
        !Array.isArray(invoiceResponse.data.data) ||
        invoiceResponse.data.data.length === 0
      ) {
        logger.log("No invoice found, attempting to create one and pay...");

        // Fetch full booking details to ensure we have all data needed for invoice creation
        try {
          const fullBookingRes = await spaceManagementService.getBookingById(booking.id);
          if (fullBookingRes?.status === 200 && fullBookingRes?.data) {
            const fullBooking = fullBookingRes.data;
            let spaceData;
            const spaceRes = await spaceManagementService.getSpaceById(fullBooking.spaceId);

            if (spaceRes?.status === 200 && spaceRes?.data) {
              spaceData = spaceRes.data;
            } else {
              logger.warn("DEBUG: Failed to fetch space details by ID, falling back to booking data");
              spaceData = fullBooking.space || fullBooking.spaceInstance;
            }

            if (!spaceData) {
              throw new Error("Space details not found for this booking");
            }

            const result = await paymentFlowHelper.initiateBookingPayment(fullBooking, spaceData);
            if (result.success) {
              // Payment initiated successfully (helper handles redirect)
              return;
            } else {
              throw new Error("Failed to initiate payment flow");
            }
          } else {
            throw new Error("Could not fetch full booking details for payment");
          }
        } catch (flowError) {
          logger.error("Auto-payment flow failed:", flowError);
          showApiMessage(
            {
              response: {
                status: 404,
                data: { message: flowError.message || "No invoice found and could not create one" },
              },
            },
            "pay booking"
          );
          setIsProcessingPayment(false);
          return;
        }
      }

      const invoice = invoiceResponse.data.data[0];
      if (!invoice.referenceCode) {
        showApiMessage(
          { response: { status: 400, data: { message: "Invoice reference code not found" } } },
          "pay booking"
        );
        setIsProcessingPayment(false);
        return;
      }

      // Initiate payment with the invoice referenceCode
      const paymentResponse = await billingService.initiatePayment({
        reference: invoice.referenceCode,
      });

      // Check if response contains checkout URL
      if (paymentResponse.data?.data?.checkoutUrl) {
        const checkoutUrl = paymentResponse.data.data.checkoutUrl;
        // Open Paystack checkout in a new tab
        const newWindow = window.open("", "_blank");
        if (newWindow) {
          newWindow.location.href = checkoutUrl;
        } else {
          // Fallback if popup blocker prevents new window
          window.location.href = checkoutUrl;
        }
      } else {
        showApiMessage({ message: "Payment initiated successfully" }, "pay booking");
        // Refresh bookings to update status
        fetchBookings(currentPage);
      }
    } catch (err) {
      logger.error("Failed to initiate payment:", err);
      showApiMessage(err, "pay booking");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const renderActionMenu = booking => {
    const user = localService.getUser();
    const isPlatformAdmin = FeatureAccess.isPlatformAdmin(user);

    // Platform Admins can only view, so hide the action menu entirely
    if (isPlatformAdmin) {
      return null;
    }

    const status = booking.status ? booking.status.toUpperCase() : "";

    // Hide menu for terminal statuses
    const isTerminalStatus = ["CANCELLED", "CANCELED", "COMPLETED", "NO_SHOW"].includes(status);
    if (isTerminalStatus) {
      return null;
    }

    const isPending = status === "PENDING";
    const isPendingPayment = status === "PENDING_PAYMENT";
    const isCheckedIn = status === "CHECKED_IN";

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={e => e.stopPropagation()}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
          {canPayNow(booking) && (
            <>
              <DropdownMenuItem
                onClick={() => handlePayBooking(booking)}
                disabled={isProcessingPayment}
                className="text-green-600 focus:text-green-600 focus:bg-green-50"
              >
                <CreditCard className="mr-2 h-4 w-4" />
                {isProcessingPayment ? "Processing..." : "Pay Now"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {canModify(booking) && (
            <DropdownMenuItem onClick={() => handleEditBooking(booking)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}

          {canCheckIn(booking) && (
            <DropdownMenuItem onClick={() => handleCheckIn(booking)}>
              <LogIn className="mr-2 h-4 w-4" />
              Check In
            </DropdownMenuItem>
          )}

          {canCheckOut(booking) && (
            <DropdownMenuItem onClick={() => handleCheckOut(booking)}>
              <LogOut className="mr-2 h-4 w-4" />
              Check Out
            </DropdownMenuItem>
          )}

          {canCancel(booking) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleCancelBooking(booking)}
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Cancel
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // Image Carousel Component for booking cards - Modern design with blue gradient fallback
  const ImageCarousel = ({ images, spaceTitle }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [hasError, setHasError] = useState(false);
    const hasMultipleImages = images && images.length > 1;

    const goToNext = e => {
      e?.stopPropagation();
      setCurrentImageIndex(prev => (prev + 1) % images.length);
      setHasError(false);
    };

    const goToPrev = e => {
      e?.stopPropagation();
      setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
      setHasError(false);
    };

    const goToImage = (index, e) => {
      e?.stopPropagation();
      setCurrentImageIndex(index);
      setHasError(false);
    };

    // Show blue gradient with icon if no images or error
    if (!images || images.length === 0 || hasError) {
      return (
        <div className="relative w-full h-48 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-t-lg overflow-hidden flex items-center justify-center border-2 border-dashed border-blue-200">
          <div className="flex flex-col items-center gap-2">
            <Building2 className="w-12 h-12 text-blue-400" />
            <span className="text-xs text-blue-500 font-medium">No images available</span>
          </div>
        </div>
      );
    }

    return (
      <div
        className="relative w-full h-48 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-t-lg overflow-hidden group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img
          src={images[currentImageIndex]}
          alt={`${spaceTitle || "Space"} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover transition-all duration-300"
          onError={() => {
            setHasError(true);
          }}
        />

        {/* Navigation Arrows - Only show if multiple images */}
        {hasMultipleImages && (
          <>
            <button
              onClick={goToPrev}
              className={`absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200 backdrop-blur-sm ${
                isHovered ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              aria-label="Previous image"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToNext}
              className={`absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200 backdrop-blur-sm ${
                isHovered ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              aria-label="Next image"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Image Indicators - Modern dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={e => goToImage(index, e)}
                  className={`rounded-full transition-all duration-300 ${
                    index === currentImageIndex
                      ? "w-8 h-1.5 bg-white shadow-md"
                      : "w-1.5 h-1.5 bg-white/60 hover:bg-white/80"
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>

            {/* Image Counter - Modern badge */}
            <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
              {currentImageIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>
    );
  };

  // Render booking card component for grid view - Sleek
  const renderBookingCard = (booking, isTodayBooking = false) => {
    logger.log("ndnksdnkdn 🔍 renderBookingCard - Rendering:", {
      id: booking.id,
      status: booking.status,
      title: booking.title,
    });

    const statusConfig = getStatusConfig(booking.status);
    const startDateTime = formatDateTime(booking.startTime);
    const endDateTime = formatDateTime(booking.endTime);
    const spaceImages = booking.spaceImages || [];

    return (
      <Card
        className={`group overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 bg-white cursor-pointer ${
          isTodayBooking ? "border-2 border-blue-500 ring-1 ring-blue-200/70" : "border border-gray-100"
        }`}
        onClick={() => handleBookingClick(booking)}
      >
        <CardContent className="p-0">
          <div className="relative h-52 overflow-hidden">
            {spaceImages.length > 0 ? (
              <img
                src={spaceImages[0]}
                alt={booking.title}
                className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
                onError={e => {
                  e.target.style.display = "none";
                  const fallback = e.target.nextElementSibling;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className={`absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 flex items-center justify-center ${
                spaceImages.length > 0 ? "hidden" : ""
              }`}
            >
              <Building2 className="w-12 h-12 text-blue-400" />
            </div>

            <div className="absolute top-3 right-3 flex items-center gap-2">
              {isTodayBooking && (
                <Badge className="bg-blue-500 text-white text-[11px] font-semibold px-2.5 py-1 shadow-md flex items-center gap-1 rounded-full">
                  <Clock className="w-3 h-3" />
                  TODAY
                </Badge>
              )}
              <Badge
                className={`${statusConfig.color} text-[11px] font-semibold px-2.5 py-1 shadow-md flex items-center gap-1 rounded-full`}
              >
                <div className={`w-2 h-2 rounded-full ${statusConfig.dotColor}`}></div>
                {booking.status
                  .toLowerCase()
                  .split("_")
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
              </Badge>
            </div>

            {spaceImages.length > 1 && (
              <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white text-[11px] font-medium px-2.5 py-1 rounded-full">
                {spaceImages.length} photos
              </div>
            )}
          </div>

          <div className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <h3 className="text-base font-semibold text-gray-900 line-clamp-1 group-hover:text-blue-600 transition-colors">
                  {booking.title}
                </h3>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{booking.location}</span>
                </div>
              </div>
              <span className="text-[11px] font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full flex-shrink-0">
                BK-{booking.id.toString().padStart(6, "0")}
              </span>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
              <div className="flex items-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="font-medium">{startDateTime.date}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-900 font-semibold">
                <Clock className="w-3.5 h-3.5 text-gray-400" />
                <span>
                  {startDateTime.time} — {endDateTime.time}
                </span>
              </div>
              {booking.spaceCapacity > 0 && (
                <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 border border-gray-200 text-[11px]">
                  {booking.spaceCapacity} ppl
                </span>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <Users className="w-3.5 h-3.5 text-gray-400" />
                <span className="truncate">Booked by {booking.user}</span>
              </div>
              <div onClick={e => e.stopPropagation()}>{renderActionMenu(booking)}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen ">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header Section */}
        {/* Header Section */}
        <div className="mb-6">
          <PageHeader
            title="My Reservations"
            totalCount={filteredBookings.length}
            subtitle="Manage and view your workspace reservations"
            rightContent={
              <>
                {/* View Mode Toggle */}
                <div className="inline-flex bg-gray-200 rounded-md">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`flex items-center justify-center p-2 transition-all duration-200 ${
                      viewMode === "grid" ? "bg-black text-white shadow-sm" : "text-black hover:bg-gray-300"
                    }`}
                    title="Grid"
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`flex items-center justify-center p-2 transition-all duration-200 ${
                      viewMode === "list" ? "bg-black text-white shadow-sm" : "text-black hover:bg-gray-300"
                    }`}
                    title="List"
                  >
                    <ListIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Order By */}
                <SortDropdown options={BOOKING_SORT_OPTIONS} value={sortOption} onChange={setSortOption} />

                {/* Filters Button */}
                <Button variant="outline" size="sm" onClick={() => setShowFilters(true)}>
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                  {activeFilters.length > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-white text-gray-900 border border-gray-300">
                      {activeFilters.length}
                    </Badge>
                  )}
                </Button>
              </>
            }
            bottomContent={
              <div className="pt-2 pb-2">
                <form onSubmit={performSearch} className="w-full">
                  <div className="relative">
                    <Input
                      type="search"
                      placeholder="Search by address, building, host org, email..."
                      value={searchValue}
                      onChange={e => setSearchValue(e.target.value)}
                      className="pl-4 pr-12 bg-white [appearance:textfield] [::-webkit-search-decoration]:hidden [::-webkit-search-cancel-button]:hidden [::-webkit-search-results-button]:hidden [::-webkit-search-results-decoration]:hidden"
                    />
                    <button
                      type={searchValue ? "button" : "submit"}
                      onClick={searchValue ? handleClearSearch : undefined}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 transition"
                      aria-label={searchValue ? "Clear search" : "Search"}
                    >
                      {searchValue ? <X className="w-4 h-4" /> : <Search className="w-4 h-4" />}
                    </button>
                  </div>
                </form>
              </div>
            }
          />
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {activeFilters.map(filter => (
              <Badge
                key={filter.id}
                variant="secondary"
                className="px-3 py-1 bg-white text-gray-900 border border-gray-300"
              >
                {filter.label}
                <button onClick={() => clearFilter(filter.id)} className="ml-2 hover:text-destructive">
                  ×
                </button>
              </Badge>
            ))}
            <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-auto py-1">
              Clear all
            </Button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex justify-center w-full">
          <div className="w-full">
            {loading ? (
              viewMode === "grid" ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div key={idx} className="h-72 rounded-xl border border-gray-200 bg-white overflow-hidden">
                      <div className="h-36 w-full bg-gray-200 animate-pulse" />
                      <div className="p-4 space-y-3">
                        <div className="h-4 w-3/4 bg-gray-200 animate-pulse rounded" />
                        <div className="h-3 w-1/2 bg-gray-200 animate-pulse rounded" />
                        <div className="flex gap-2">
                          <div className="h-8 w-16 bg-gray-200 animate-pulse rounded-full" />
                          <div className="h-8 w-20 bg-gray-200 animate-pulse rounded-full" />
                        </div>
                        <div className="h-3 w-1/3 bg-gray-200 animate-pulse rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <div key={idx} className="rounded-xl border border-gray-200 bg-white p-4 flex items-center gap-4">
                      <div className="h-16 w-16 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-2/3 bg-gray-200 animate-pulse rounded" />
                        <div className="h-3 w-1/2 bg-gray-200 animate-pulse rounded" />
                        <div className="h-3 w-1/3 bg-gray-200 animate-pulse rounded" />
                      </div>
                      <div className="h-8 w-20 bg-gray-200 animate-pulse rounded-full flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground">
                  {searchValue
                    ? "No reservations found. Try adjusting your search or filters"
                    : timeFilter === "upcoming"
                      ? "No upcoming reservations"
                      : timeFilter === "active"
                        ? "No active reservations"
                        : timeFilter === "past"
                          ? "No past reservations"
                          : "No reservations found"}
                </p>
              </div>
            ) : (
              <>
                {/* Today's Bookings Section - Prominent */}
                {viewMode !== "list" && (loadingTodays || filteredTodaysBookings.length > 0) && (
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                        <h2 className="text-xl font-bold text-gray-900">Today's Bookings</h2>
                      </div>
                      <Badge className="bg-blue-500 text-white px-3 py-1">
                        {filteredTodaysBookings.length} {filteredTodaysBookings.length === 1 ? "booking" : "bookings"}
                      </Badge>
                    </div>
                    {loadingTodays ? (
                      viewMode === "grid" ? (
                        <div className="relative">
                          <div className="flex gap-4 overflow-hidden pb-4 snap-x snap-mandatory">
                            {Array.from({ length: 4 }).map((_, idx) => (
                              <div
                                key={`today-skel-${idx}`}
                                className="flex-shrink-0 w-full max-w-xs min-w-[280px] snap-center rounded-xl border border-gray-200 bg-white overflow-hidden"
                              >
                                <div className="h-24 w-full bg-gray-200 animate-pulse" />
                                <div className="p-4 space-y-3">
                                  <div className="h-4 w-2/3 bg-gray-200 animate-pulse rounded" />
                                  <div className="h-3 w-1/2 bg-gray-200 animate-pulse rounded" />
                                  <div className="flex gap-2">
                                    <div className="h-6 w-14 bg-gray-200 animate-pulse rounded-full" />
                                    <div className="h-6 w-16 bg-gray-200 animate-pulse rounded-full" />
                                  </div>
                                  <div className="h-3 w-1/3 bg-gray-200 animate-pulse rounded" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {Array.from({ length: 3 }).map((_, idx) => (
                            <div
                              key={`today-row-skel-${idx}`}
                              className="rounded-xl border border-gray-200 bg-white p-4 flex items-center gap-4"
                            >
                              <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                              <div className="flex-1 grid grid-cols-3 gap-3 items-center">
                                <div className="h-4 w-3/4 bg-gray-200 animate-pulse rounded" />
                                <div className="h-3 w-2/3 bg-gray-200 animate-pulse rounded" />
                                <div className="h-3 w-1/2 bg-gray-200 animate-pulse rounded" />
                              </div>
                              <div className="h-8 w-16 bg-gray-200 animate-pulse rounded-full flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      )
                    ) : viewMode === "grid" ? (
                      <div className="relative">
                        <div
                          className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory"
                          role="region"
                          aria-label="Today's bookings carousel"
                        >
                          {filteredTodaysBookings.map(booking => (
                            <div
                              key={`today-${booking.id}`}
                              className="flex-shrink-0 w-full max-w-xs min-w-[280px] snap-center"
                            >
                              {renderBookingCard(booking, true)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto mb-6">
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="font-semibold">Reservation ID</TableHead>
                              <TableHead className="font-semibold">Space Name</TableHead>
                              <TableHead className="font-semibold">Location</TableHead>
                              <TableHead className="font-semibold">Booked By</TableHead>
                              <TableHead className="font-semibold">Start Time</TableHead>
                              <TableHead className="font-semibold">End Time</TableHead>
                              <TableHead className="font-semibold">Status</TableHead>
                              <TableHead className="font-semibold">Notes</TableHead>
                              <TableHead className="font-semibold">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredTodaysBookings.map(booking => {
                              const statusConfig = getStatusConfig(booking.status);
                              return (
                                <TableRow
                                  key={booking.id}
                                  className="hover:bg-muted/50 cursor-pointer bg-blue-50/30 border-l-4 border-l-blue-500"
                                  onClick={() => handleBookingClick(booking)}
                                >
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Badge className="bg-blue-500 text-white text-xs">TODAY</Badge>
                                      <span className="font-semibold text-primary">
                                        BK-{booking.id.toString().padStart(6, "0")}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <div className="font-medium">{booking.title}</div>
                                      <div className="text-xs text-muted-foreground">
                                        Instance #{booking.spaceInstanceId}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="max-w-[220px]">
                                      <div className="text-sm">{booking.location}</div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-3">
                                      <img
                                        src={booking.avatar}
                                        alt={booking.user}
                                        className="w-9 h-9 rounded-full object-cover ring-2 ring-border"
                                        onError={e => {
                                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                            booking.user
                                          )}&background=random&color=fff`;
                                        }}
                                      />
                                      <div className="min-w-0 flex-1">
                                        <div className="font-medium text-sm truncate" title={booking.user}>
                                          {booking.user}
                                        </div>
                                        {booking.email && (
                                          <div
                                            className="text-xs text-muted-foreground truncate max-w-[180px]"
                                            title={booking.email}
                                          >
                                            {booking.email}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <div className="text-sm font-medium">
                                        {formatDateTime(booking.startTime).date}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {formatDateTime(booking.startTime).time}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <div className="text-sm font-medium">{formatDateTime(booking.endTime).date}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {formatDateTime(booking.endTime).time}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={statusConfig.variant} className={statusConfig.color}>
                                      {booking.status
                                        .toLowerCase()
                                        .split("_")
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                        .join(" ")}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <div className="max-w-[200px]">
                                      {booking.notes ? (
                                        <div className="text-sm line-clamp-2" title={booking.notes}>
                                          {booking.notes}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-muted-foreground italic">—</span>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div onClick={e => e.stopPropagation()}>{renderActionMenu(booking)}</div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}

                {/* Other Bookings Section */}
                {otherBookings.length > 0 && (
                  <div>
                    {filteredTodaysBookings.length > 0 && (
                      <div className="flex items-center gap-3 mb-4">
                        <h2 className="text-lg font-semibold text-gray-700">All Bookings</h2>
                        <div className="flex-1 h-px bg-gray-200"></div>
                      </div>
                    )}
                    {viewMode === "grid" ? (
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {otherBookings.map(booking => (
                          <div key={booking.id}>{renderBookingCard(booking, false)}</div>
                        ))}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="font-semibold">Reservation ID</TableHead>
                              <TableHead className="font-semibold">Space Name</TableHead>
                              <TableHead className="font-semibold">Location</TableHead>
                              <TableHead className="font-semibold">Booked By</TableHead>
                              <TableHead className="font-semibold">Start Time</TableHead>
                              <TableHead className="font-semibold">End Time</TableHead>
                              <TableHead className="font-semibold">Status</TableHead>
                              <TableHead className="font-semibold">Notes</TableHead>
                              <TableHead className="font-semibold">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {otherBookings.map(booking => {
                              const statusConfig = getStatusConfig(booking.status);
                              return (
                                <TableRow
                                  key={booking.id}
                                  className="hover:bg-muted/50 cursor-pointer"
                                  onClick={() => handleBookingClick(booking)}
                                >
                                  <TableCell>
                                    <span className="font-semibold text-primary">
                                      BK-{booking.id.toString().padStart(6, "0")}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <div className="font-medium">{booking.title}</div>
                                      <div className="text-xs text-muted-foreground">
                                        Instance #{booking.spaceInstanceId}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="max-w-[220px]">
                                      <div className="text-sm">{booking.location}</div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-3">
                                      <img
                                        src={booking.avatar}
                                        alt={booking.user}
                                        className="w-9 h-9 rounded-full object-cover ring-2 ring-border"
                                        onError={e => {
                                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                            booking.user
                                          )}&background=random&color=fff`;
                                        }}
                                      />
                                      <div className="min-w-0 flex-1">
                                        <div className="font-medium text-sm truncate" title={booking.user}>
                                          {booking.user}
                                        </div>
                                        {booking.email && (
                                          <div
                                            className="text-xs text-muted-foreground truncate max-w-[180px]"
                                            title={booking.email}
                                          >
                                            {booking.email}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <div className="text-sm font-medium">
                                        {formatDateTime(booking.startTime).date}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {formatDateTime(booking.startTime).time}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <div className="text-sm font-medium">{formatDateTime(booking.endTime).date}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {formatDateTime(booking.endTime).time}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={statusConfig.variant} className={statusConfig.color}>
                                      {booking.status.toLowerCase().replace("_", " ")}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <div className="max-w-[200px]">
                                      {booking.notes ? (
                                        <div className="text-sm line-clamp-2" title={booking.notes}>
                                          {booking.notes}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-muted-foreground italic">—</span>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div onClick={e => e.stopPropagation()}>{renderActionMenu(booking)}</div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPageSize > 0 && (
          <div className="flex justify-center mt-6">
            <Pagination
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={totalPageSize}
              pageSize={itemsPerPage}
              siblingCount={1}
              onPageChange={changePage}
            />
          </div>
        )}
      </div>

      {/* Filter Dialog */}
      <BookingFilterDialog
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApplyFilters={applyFilters}
        onClearFilters={clearAllFilters}
        initialFilters={{
          status: bookingStatus,
          timeFilter: timeFilter,
        }}
      />

      {/* Cancel Booking Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
            <DialogDescription>
              {bookingToCancel && (
                <>
                  Are you sure you want to cancel booking for{" "}
                  {bookingToCancel.title || `booking BK-${bookingToCancel.id.toString().padStart(6, "0")}`}?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelDialog(false);
                setBookingToCancel(null);
              }}
              disabled={isCancelling}
            >
              Back
            </Button>
            <Button onClick={confirmCancelBooking} disabled={isCancelling} variant="destructive">
              {isCancelling ? "Cancelling..." : "Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Booking</DialogTitle>
            <DialogDescription>
              Move your booking window. The duration remains fixed at{" "}
              {Math.round((editOriginalDurationMs / (1000 * 60 * 60)) * 10) / 10} hours.
            </DialogDescription>
          </DialogHeader>

          {editingBooking && (
            <div className="space-y-6 py-4">
              {editingBooking?.status === "CHECKED_IN" && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  ℹ️ This booking has been checked-in. You can only modify the end date and end time.
                </div>
              )}

              {/* Duration Info */}
              {editOriginalDurationMs && (
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Fixed Duration</span>
                  </div>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-none">
                    {Math.round((editOriginalDurationMs / (1000 * 60 * 60)) * 10) / 10} hours
                  </Badge>
                </div>
              )}

              {/* Start Date/Time */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-gray-400" />
                    Start Date
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={editingBooking?.status === "CHECKED_IN"}
                        className={`w-full justify-start text-left font-normal border-gray-300 ${!editFormData.startDate ? "text-gray-400" : ""}`}
                      >
                        {editFormData.startDate ? editFormData.startDate.toLocaleDateString() : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editFormData.startDate}
                        onSelect={handleEditStartDateChange}
                        disabled={date => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        modifiers={editCalendarModifiers}
                        modifiersStyles={editCalendarModifiersStyles}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    Start Time
                  </label>
                  <Input
                    type="time"
                    value={editFormData.startTime}
                    onChange={e => handleEditStartTimeChange(e.target.value)}
                    disabled={editingBooking?.status === "CHECKED_IN"}
                    className="w-full border-gray-300"
                  />
                </div>
              </div>

              {/* End Date/Time */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-gray-400" />
                    End Date
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal border-gray-300 ${!editFormData.endDate ? "text-gray-400" : ""}`}
                      >
                        {editFormData.endDate ? editFormData.endDate.toLocaleDateString() : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editFormData.endDate}
                        onSelect={handleEditEndDateChange}
                        disabled={date => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        modifiers={editCalendarModifiers}
                        modifiersStyles={editCalendarModifiersStyles}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    End Time
                  </label>
                  <Input
                    type="time"
                    value={editFormData.endTime}
                    onChange={e => handleEditEndTimeChange(e.target.value)}
                    className="w-full border-gray-300"
                  />
                </div>
              </div>

              {/* Emails */}
              {FeatureAccess.hasAttendeeAccess(localService.getUser()) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Emails of Invitees</label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={emailInput}
                      onChange={e => setEmailInput(e.target.value)}
                      onKeyPress={e => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddEmail();
                        }
                      }}
                      className="flex-1"
                    />
                    <Button type="button" onClick={handleAddEmail} variant="outline">
                      Add
                    </Button>
                  </div>
                  {editFormData.emails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editFormData.emails.map((email, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1">
                          {email}
                          <button
                            type="button"
                            onClick={() => handleRemoveEmail(email)}
                            className="ml-1 hover:text-red-600"
                          >
                            <XCircle className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  Notes
                </label>
                <Textarea
                  value={editFormData.notes}
                  onChange={e => setEditFormData({ ...editFormData, notes: e.target.value })}
                  placeholder="Add any notes or special requirements..."
                  className="min-h-[100px] border-gray-300"
                  rows={4}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Details Modal - Beautiful Modern Design */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 bg-white rounded-xl">
          {loadingDetails ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner message="Loading booking details..." />
            </div>
          ) : bookingDetails ? (
            <>
              {/* Hero Image Section */}
              <div
                className="relative w-full h-64 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden"
                style={{
                  transform: `translateY(${-heroParallax}px)`,
                  opacity: Math.max(0, 1 - heroParallax / 180),
                  willChange: "transform, opacity",
                }}
              >
                {bookingDetails.spaceImages && bookingDetails.spaceImages.length > 0 ? (
                  <div className="relative w-full h-full">
                    <ImageCarousel images={bookingDetails.spaceImages} spaceTitle={bookingDetails.title} />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
                    <Building2 className="w-16 h-16 text-blue-400" />
                  </div>
                )}

                {/* Status Badge - Top Right */}
                <div className="absolute top-4 right-4">
                  {(() => {
                    const statusConfig = getStatusConfig(bookingDetails.status);
                    const StatusIcon = statusConfig.icon;
                    return (
                      <Badge
                        variant={statusConfig.variant}
                        className={`${statusConfig.color} flex items-center gap-1.5 px-3 py-1.5 shadow-lg backdrop-blur-sm`}
                      >
                        <StatusIcon className="w-3.5 h-3.5" />
                        <span className="font-semibold text-xs">
                          {bookingDetails.status.toLowerCase().replace("_", " ")}
                        </span>
                      </Badge>
                    );
                  })()}
                </div>
              </div>

              {/* Content Section */}
              <div className="flex-1 overflow-y-auto" ref={bookingDetailsScrollRef} onScroll={handleDetailsScroll}>
                <div className="p-6 space-y-5">
                  {/* Header Section */}
                  <div className="space-y-3 pb-4 border-b border-gray-100">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">{bookingDetails.title}</h2>
                      <div className="flex items-center gap-4 flex-wrap">
                        {bookingDetails.spaceCapacity > 0 && (
                          <div className="flex items-center gap-1.5 text-sm text-gray-600 bg-white border border-gray-200 px-3 py-1.5 rounded-full">
                            <Users className="w-4 h-4" />
                            <span className="font-medium">{bookingDetails.spaceCapacity} people</span>
                          </div>
                        )}
                        {bookingDetails.spacePrice > 0 && (
                          <div className="text-sm font-semibold text-gray-900 bg-white border border-gray-200 px-3 py-1.5 rounded-full text-blue-700">
                            {bookingDetails.spaceCurrency} {bookingDetails.spacePrice.toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Reservation ID</span>
                      <span className="text-sm font-bold text-blue-600">
                        BK-{bookingDetails.id.toString().padStart(6, "0")}
                      </span>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Location */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <MapPin className="w-3.5 h-3.5" />
                        Location
                      </div>
                      <p className="text-sm text-gray-900 leading-relaxed">{bookingDetails.location}</p>
                    </div>

                    {/* Booked By */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <User className="w-3.5 h-3.5" />
                        Booked By
                      </div>
                      <div className="flex items-center gap-3">
                        <img
                          src={bookingDetails.avatar}
                          alt={bookingDetails.user}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-200"
                          onError={e => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              bookingDetails.user
                            )}&background=random&color=fff`;
                          }}
                        />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{bookingDetails.user}</p>
                          {bookingDetails.email && <p className="text-xs text-gray-600">{bookingDetails.email}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Category & Subcategory */}
                    <div className="space-y-2 col-span-1 md:col-span-2 pt-2 border-t border-gray-100 grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                          Category
                        </div>
                        <p className="text-sm font-semibold text-gray-900">
                          {bookingDetails.space?.category?.name || bookingDetails.space?.categoryName || "N/A"}
                        </p>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                          Subcategory
                        </div>
                        <p className="text-sm font-semibold text-gray-900">
                          {bookingDetails.subCategoryName ||
                            bookingDetails.space?.subCategory?.name ||
                            bookingDetails.space?.subCategoryName ||
                            "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Date & Time Section */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                      <CalendarIcon className="w-3.5 h-3.5" />
                      Date & Time
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1.5">Start Time</p>
                        <p className="text-base font-bold text-gray-900 mb-0.5">
                          {formatDateTime(bookingDetails.startTime).date}
                        </p>
                        <p className="text-sm text-gray-600">{formatDateTime(bookingDetails.startTime).time}</p>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1.5">End Time</p>
                        <p className="text-base font-bold text-gray-900 mb-0.5">
                          {formatDateTime(bookingDetails.endTime).date}
                        </p>
                        <p className="text-sm text-gray-600">{formatDateTime(bookingDetails.endTime).time}</p>
                      </div>
                    </div>
                  </div>

                  {/* Addons Breakdown */}
                  {bookingDetails.addons && bookingDetails.addons.length > 0 && (
                    <div className="pt-4 border-t border-gray-100">
                      <AddonsBreakdown
                        addons={bookingDetails.addons}
                        currency={bookingDetails.spaceCurrency || bookingDetails.currency || "GHS"}
                      />
                    </div>
                  )}

                  {/* Notes */}
                  {bookingDetails.notes && (
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        <FileText className="w-3.5 h-3.5" />
                        Notes
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {bookingDetails.notes}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Space Description */}
                  {bookingDetails.spaceDescription && (
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        <Building2 className="w-3.5 h-3.5" />
                        Space Description
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {bookingDetails.spaceDescription}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Booked At */}
                  {bookingDetails.createdAt && (
                    <div className="pt-4 border-t border-gray-100 text-center">
                      <p className="text-xs text-gray-500">
                        Booked on {formatDateTime(new Date(bookingDetails.createdAt)).date} at{" "}
                        {formatDateTime(new Date(bookingDetails.createdAt)).time}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
                <Button
                  onClick={() => setShowBookingModal(false)}
                  className="px-6 bg-gray-900 hover:bg-gray-800 text-white"
                >
                  Close
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 px-8">
              <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-sm font-medium text-gray-900 mb-1">No booking details available</p>
              <p className="text-xs text-gray-600 text-center">Unable to load booking information</p>
              <Button onClick={() => setShowBookingModal(false)} variant="outline" className="mt-4">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Booking;
